import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { srcSetCandidates } from "../domain/images.ts";
import { ownerKey, useOwnerId } from "../features/auth/owner.ts";
import { api, query, type Schemas, type UploadProgress } from "./client.ts";
import { NOTIFICATIONS_KEY } from "./notifications.ts";

/**
 * Un hook por lectura y uno por mutacion (SPEC 15.2). Los tipos salen del
 * contrato generado: si un DTO del servidor cambia y no se regeneran, esto no
 * compila, que es exactamente la guarda que SPEC 5 pide.
 *
 * EL DUENO VA EN CADA LECTURA (ADR 0005). Toda lectura de la bitacora acepta
 * `?owner=` y aqui se anade solo, leido del contexto (`features/auth/owner.ts`):
 * las pantallas no lo pasan ni lo saben. Y va DENTRO de la clave de cache,
 * justo detras del prefijo: sin el, cambiar de dueno ensenaria durante un
 * instante —o para siempre, con `staleTime`— las operaciones del anterior bajo
 * el nombre del nuevo. Las invalidaciones por prefijo (`["trades"]`,
 * `["summary"]`) siguen alcanzando a todos los duenos, que es lo que se quiere:
 * una escritura cambia lo propio, y lo propio es uno de ellos.
 */

export type Lookups = Schemas["LookupsResponse"];
export type TradeDetail = Schemas["TradeDetailDto"];
export type TradeListItem = Schemas["TradeListItemDto"];
export type TradeList = Schemas["TradeListResponse"];
export type TradeImage = Schemas["TradeImageDto"];
export type ImageKind = Schemas["ImageKind"];
export type Instrument = Schemas["InstrumentDto"];
export type CatalogItem = Schemas["CatalogItemDto"];
export type PlanRule = Schemas["PlanRuleDto"];
export type Summary = Schemas["DashboardSummaryDto"];
export type EquityPoint = Schemas["EquityPointDto"];
export type Breakdown = Schemas["BreakdownResponse"];
export type BreakdownRow = Schemas["BreakdownRowDto"];
export type BreakdownDimension = Schemas["BreakdownDimension"];
export type PeriodGranularity = Schemas["PeriodGranularity"];
export type PeriodTotals = Schemas["PeriodTotalsResponse"];
export type PeriodRow = Schemas["PeriodTotalsRowDto"];
export type PipsSource = Schemas["PipsSource"];
export type RecalculateResult = Schemas["RecalculateResponse"];
export type DatabaseHealth = Schemas["DatabaseHealthResponse"];
export type TableStorage = Schemas["TableStorageDto"];
export type DemoRowCount = Schemas["DemoRowCountDto"];
export type ImageStorage = Schemas["ImageStorageDto"];
export type FactoryResetResult = Schemas["FactoryResetResponse"];
export type DemoDataResult = Schemas["DemoDataResponse"];
export type DemoDataRemoved = Schemas["DemoDataRemovedResponse"];
export type DatabaseRestore = Schemas["DatabaseRestoreDto"];
export type User = Schemas["UserDto"];
export type UserRole = Schemas["UserRole"];
export type SessionPolicy = Schemas["SessionPolicyDto"];

const keys = {
  session: ["session"] as const,
  lookups: ["lookups"] as const,
  trades: (owner: string | undefined, filters: TradeFilters) =>
    ["trades", ownerKey(owner), filters] as const,
  trade: (owner: string | undefined, id: string) => ["trade", ownerKey(owner), id] as const,
  summary: ["summary"] as const,
  equity: ["equity"] as const,
  breakdown: ["breakdown"] as const,
  periods: ["periods"] as const,
  databaseHealth: ["database-health"] as const,
  users: ["users"] as const,
  sessionPolicy: ["session-policy"] as const,
};

export type TradeFilters = {
  status?: string;
  outcome?: string;
  instrumentId?: string;
  /**
   * Cuantas operaciones por pagina. Va DENTRO de los filtros y no como argumento
   * aparte porque la clave de cache es el objeto entero: con el tamano fuera, la
   * bitacora y la Biblioteca compartirian cache y la segunda veria paginas de
   * cincuenta que ella no pidio.
   */
  pageSize?: number;
};

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: () => api.get<Schemas["SessionResponse"]>("/api/v1/auth/session"),
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * TODOS los desplegables en una peticion (SPEC 11). En el movil, con conexion
 * irregular, nueve peticiones para abrir un formulario son nueve oportunidades
 * de que algo falle.
 *
 * Los catalogos casi no cambian, asi que se cachean cinco minutos.
 */
export function useLookups() {
  return useQuery({
    queryKey: keys.lookups,
    queryFn: () => api.get<Lookups>("/api/v1/settings/lookups"),
    staleTime: 5 * 60_000,
  });
}

/**
 * El listado paginado POR CURSOR (SPEC 19.2). Es infinito de verdad: cada pagina
 * trae su `nextCursor` y la bitacora pide la siguiente cuando se le dice, en vez
 * de prometer un historial entero y ensenar solo las primeras cincuenta.
 *
 * `enabled` apaga la peticion cuando la pantalla no va a pintar operaciones: lo
 * que no se ve no se pide, y un fallo de lo que no se ve no rompe nada.
 */
export function useTrades(filters: TradeFilters, enabled = true) {
  const owner = useOwnerId();

  return useInfiniteQuery({
    queryKey: keys.trades(owner, filters),
    queryFn: ({ pageParam }) =>
      api.get<TradeList>(
        `/api/v1/trades${query({
          status: filters.status,
          outcome: filters.outcome,
          instrumentId: filters.instrumentId,
          cursor: pageParam,
          pageSize: filters.pageSize ?? 50,
          owner,
        })}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: TradeList) => last.nextCursor ?? undefined,
    enabled,
  });
}

export function useTrade(id: string | undefined) {
  const owner = useOwnerId();

  return useQuery({
    queryKey: keys.trade(owner, id ?? ""),
    queryFn: () => api.get<TradeDetail>(`/api/v1/trades/${id}${query({ owner })}`),
    enabled: id !== undefined,
  });
}

/**
 * Los detalles de VARIAS operaciones a la vez.
 *
 * Existe por la Biblioteca, y no es gratis: una peticion por operacion. El
 * contrato no trae las capturas en `TradeListItemDto` ni hay un listado de
 * imagenes, asi que la unica forma de saber que capturas tiene el usuario es
 * abrir cada operacion. Lo que abarata la cuenta es que la clave es la MISMA que
 * la de `useTrade`: lo que la Biblioteca trae ya queda servido para cuando se
 * abra esa operacion, y al reves.
 *
 * Si esto llega a doler, lo que hay que cambiar es el contrato —un
 * `GET /images` con su cursor, o el recuento en el DTO del listado—, no esta
 * pantalla.
 *
 * Un fallo en una no vacia la rejilla: esa operacion no aporta capturas y las
 * demas siguen, con el recuento de las que fallaron a la vista.
 */
export function useTradeDetails(ids: readonly string[]): {
  details: TradeDetail[];
  pending: number;
  failed: number;
} {
  const owner = useOwnerId();

  return useQueries({
    queries: ids.map((id) => ({
      queryKey: keys.trade(owner, id),
      queryFn: () => api.get<TradeDetail>(`/api/v1/trades/${id}${query({ owner })}`),
      staleTime: 60_000,
    })),
    combine: (results) => ({
      details: results.flatMap((result) => (result.data === undefined ? [] : [result.data])),
      pending: results.filter((result) => result.isPending).length,
      failed: results.filter((result) => result.error !== null).length,
    }),
  });
}

/**
 * La PRIMERA captura de una operacion, para la miniatura de la tarjeta
 * compacta.
 *
 * Cuesta una peticion por tarjeta y no hay forma de que cueste menos: el
 * contrato no trae las capturas en `TradeListItemDto`, que es el mismo motivo
 * por el que la Biblioteca abre cada operacion. Lo que lo hace aceptable es que
 * la clave es la MISMA que la de `useTrade` —lo que se trae aqui queda servido
 * para cuando se abra esa operacion, y al reves— y que solo se pide de las
 * tarjetas que estan a la vista: el modo ramas pinta el dia elegido, no los
 * doscientos.
 *
 * Si esto llega a doler, lo que hay que cambiar es el CONTRATO —la miniatura o
 * su recuento en el DTO del listado—, no la tarjeta.
 */
export function useFirstTradeImage(tradeId: string): {
  image: TradeImage | undefined;
  images: readonly TradeImage[];
  pending: boolean;
} {
  const owner = useOwnerId();
  const detail = useQuery({
    queryKey: keys.trade(owner, tradeId),
    queryFn: () => api.get<TradeDetail>(`/api/v1/trades/${tradeId}${query({ owner })}`),
    staleTime: 60_000,
  });

  const images = [...(detail.data?.images ?? [])].sort(
    (left, right) => left.sortOrder - right.sortOrder,
  );

  // Se devuelven TODAS ademas de la primera. El detalle ya viene entero en la
  // misma respuesta, asi que las flechas de la tarjeta -pasar de captura sin
  // abrir la operacion- no cuestan ni una peticion mas: lo caro era pedir el
  // detalle, y eso ya se hacia para la miniatura.
  return { image: images[0], images, pending: detail.isPending };
}

/**
 * La URL de una captura. El nombre del archivo SALE DEL CONTENIDO, no de la
 * operacion: la misma captura en dos operaciones es un solo par de archivos, y
 * por eso `/media` cuelga del hash y no de `/trades/{id}`.
 *
 * Dos variantes y ni una mas (SPEC 14): `thumb` son 400 px y `view`, 1920. En
 * una rejilla, `view` seria un mega por celda.
 *
 * El dueno viaja tambien aqui: una captura de la bitacora de otro se sirve con
 * `?owner=`, y sin el el servidor busca el hash entre las propias y responde
 * 404. Es un hook y no una funcion para que ninguna `<img>` pueda olvidarse.
 */
export type MediaVariant = "thumb" | "view";

export function useMediaUrl(): (contentHash: string, variant: MediaVariant) => string {
  const owner = useOwnerId();

  return (contentHash, variant) => `/api/v1/media/${contentHash}/${variant}${query({ owner })}`;
}

/**
 * Las DOS variantes en un `srcset`, para que el navegador elija.
 *
 * ESTO ARREGLA UN FALLO QUE EL PROPIETARIO VIO: "la imagen se ve bien fea
 * deteriorada cuando se ve en el timeline o ramas, tiene muy mala calidad". La
 * causa era pedir siempre `thumb` -400 px- y estirarla: en la tarjeta del feed
 * ocupa unos 722 px de ancho en un portatil, y en un telefono de densidad 3 una
 * miniatura de 295 px de lado pide casi 900 px reales. Un 400 estirado al doble
 * o al triple se ve exactamente como el propietario lo describio.
 *
 * Con `srcset` y `sizes` la decision la toma el navegador, que es el unico que
 * sabe el ancho final Y la densidad de la pantalla: coge `thumb` cuando basta y
 * `view` cuando hace falta, sin que esta capa adivine nada. Donde `thumb`
 * alcanzaba, se sigue descargando `thumb`.
 *
 * EL COSTE SE MIDIO, no se supuso, porque la primera version de este comentario
 * advertia de un gasto de ancho de banda que resulto no existir. Para un grafico
 * de 1200x700: `thumb` pesa **1,8 KB** y `view` **8,2 KB**. Son 6 KB mas por
 * imagen, en WebP, y se pagan una sola vez -la URL sale del hash y se cachea un
 * ano- y solo de lo que entra en pantalla, porque van con `loading="lazy"`. Una
 * bitacora de veinte tarjetas a plena calidad son unos 165 KB.
 *
 * Asi que NO hace falta una tercera variante intermedia. Si algun dia entraran
 * fotografias en vez de graficos -que comprimen muchisimo peor- habria que volver
 * a medir; lo que no se hace nunca es volver a estirar la pequena.
 */
export function useMediaSrcSet(): (contentHash: string, viewWidth: number) => string {
  const mediaUrl = useMediaUrl();

  /* La aritmetica vive en `domain/images.ts` y tiene sus tests: cual variante se
     ofrece y con que ancho se declara es lo unico de esto que se puede probar sin
     un navegador, y de ahi depende que el navegador elija bien. Aqui solo se le
     ponen las URL delante. */
  return (contentHash, viewWidth) =>
    srcSetCandidates(viewWidth)
      .map(({ variant, width }) => `${mediaUrl(contentHash, variant)} ${width}w`)
      .join(", ");
}

/**
 * La exportacion CSV (SPEC 19.5) de la bitacora que se esta mirando. Es un
 * `<a href>` y no un `fetch`, asi que el dueno tiene que ir escrito en el
 * enlace.
 */
export function useExportUrl(): string {
  const owner = useOwnerId();

  return `/api/v1/export/trades.csv${query({ owner })}`;
}

/**
 * El ancho y el alto de la miniatura, calculados a partir de los de `view`, que
 * son los que trae el DTO.
 *
 * Sirven para el `width`/`height` del `<img>`: sin ellos, el navegador no sabe
 * cuanto hueco reservar y la rejilla salta cuando cada imagen termina de cargar.
 * `cwebp` no amplia lo que ya es mas estrecho que el objetivo, asi que la
 * miniatura mide lo que mide `view` cuando esta no llega a 400.
 */
const THUMB_WIDTH = 400;

export function thumbSize(image: {
  readonly width: number;
  readonly height: number;
}): { width: number; height: number } {
  if (image.width <= 0 || image.height <= 0) {
    return { width: THUMB_WIDTH, height: Math.round((THUMB_WIDTH * 10) / 16) };
  }

  const width = Math.min(THUMB_WIDTH, image.width);

  return { width, height: Math.max(1, Math.round((image.height * width) / image.width)) };
}

/**
 * Subir UNA captura (SPEC 14 y 15.6).
 *
 * Devuelve una funcion y no una mutacion de TanStack Query a proposito, y es la
 * unica excepcion de este archivo: `useMutation` guarda UN estado —un
 * `isPending`, un `error`, un progreso—, y aqui hay hasta cinco archivos en la
 * cola, cada uno con su barra y su error. Con una sola mutacion, el segundo
 * archivo pisaria el estado del primero y la pantalla mentiria sobre cual esta
 * subiendo. El estado por archivo lo lleva la pantalla, que es de quien es.
 *
 * Lo que si hace el hook es lo de una mutacion de verdad: dejar la cache al dia.
 * La respuesta trae la imagen creada entera, asi que se MEZCLA en el detalle que
 * ya esta en cache y la captura aparece al instante, sin recargar nada. Y no se
 * invalida la operacion: subir una captura no toca su `version` —lo comprobado
 * en el repositorio del servidor—, asi que no hay nada que releer.
 */
export type UploadImageInput = {
  file: File;
  /**
   * Uno de los `ImageKind` del contrato, o la cadena vacia. VACIO SIGNIFICA
   * `other`, no un error: el servidor lo dice con esas palabras, porque subir la
   * captura es lo importante y clasificarla se puede hacer despues.
   */
  kind: ImageKind | "";
  onProgress: (progress: UploadProgress) => void;
};

export function useUploadTradeImage(tradeId: string): (input: UploadImageInput) => Promise<TradeImage> {
  const client = useQueryClient();
  const owner = useOwnerId();

  return async (input: UploadImageInput) => {
    const form = new FormData();

    /* Los nombres de las partes son los del contrato —`file`, `kind`— y de eso
       depende que un 400 caiga junto al campo que lo provoco (SPEC 5). El
       `Content-Type` NO se pone: lo escribe el navegador con su `boundary`. */
    form.append("file", input.file, input.file.name);
    form.append("kind", input.kind);

    const image = await api.upload<TradeImage>(
      `/api/v1/trades/${tradeId}/images`,
      form,
      input.onProgress,
    );

    client.setQueryData<TradeDetail>(keys.trade(owner, tradeId), (current) =>
      current === undefined ? current : { ...current, images: [...current.images, image] },
    );

    return image;
  };
}

/**
 * Borrar una captura. Los ARCHIVOS solo se borran si ninguna otra entrada usa
 * ese mismo contenido, y eso lo decide el servidor: aqui solo desaparece la fila
 * de esta operacion.
 */
export function useDeleteTradeImage(tradeId: string) {
  const client = useQueryClient();
  const owner = useOwnerId();

  return useMutation({
    mutationFn: (imageId: string) =>
      api.del<void>(`/api/v1/trades/${tradeId}/images/${imageId}`),
    onSuccess: (_result, imageId) => {
      client.setQueryData<TradeDetail>(keys.trade(owner, tradeId), (current) =>
        current === undefined
          ? current
          : { ...current, images: current.images.filter((image) => image.id !== imageId) },
      );
    },
  });
}

/**
 * EL ORDEN DE LA GALERIA. La primera captura es la portada: es la que el
 * timeline y las ramas usan para la publicacion.
 *
 * Se manda la LISTA ENTERA y no «mueve esta una posicion»: la peticion dice como
 * queda, asi que dos toques rapidos seguidos no se pisan, y el conjunto completo
 * es ademas el unico control de concurrencia que tiene la galeria, que no lleva
 * `version` propia.
 *
 * La cache se pinta ANTES de que el servidor responda —ordenando lo que ya hay y
 * reescribiendo `sortOrder`— porque mover una miniatura tiene que verse al
 * instante. Si el servidor dice que no, `onError` deja la galeria como estaba:
 * un orden que se queda puesto despues de un 409 ensena un orden que no existe.
 */
export function useReorderTradeImages(tradeId: string) {
  const client = useQueryClient();
  const owner = useOwnerId();

  return useMutation({
    mutationFn: (imageIds: readonly string[]) =>
      api.put<void>(`/api/v1/trades/${tradeId}/images/order`, { imageIds }),
    onMutate: (imageIds) => {
      const key = keys.trade(owner, tradeId);
      const previous = client.getQueryData<TradeDetail>(key);

      client.setQueryData<TradeDetail>(key, (current) =>
        current === undefined ? current : { ...current, images: ordered(current.images, imageIds) },
      );

      return { previous };
    },
    onError: (_error, _imageIds, context) => {
      if (context?.previous !== undefined) {
        client.setQueryData<TradeDetail>(keys.trade(owner, tradeId), context.previous);
      }
    },
  });
}

/**
 * Las capturas en el orden que dice `imageIds`, con su `sortOrder` reescrito.
 *
 * Lo que la lista NO nombre se queda al final y en el orden que tenia: el
 * servidor rechazaria esa lista, y hasta que lo diga es mejor no hacer
 * desaparecer una miniatura de la pantalla.
 */
function ordered(
  images: readonly TradeImage[],
  imageIds: readonly string[],
): TradeImage[] {
  const named = imageIds
    .map((id) => images.find((image) => image.id === id))
    .filter((image): image is TradeImage => image !== undefined);

  const rest = images.filter((image) => !imageIds.includes(image.id));

  return [...named, ...rest].map((image, index) => ({ ...image, sortOrder: index }));
}

/**
 * Las lecturas del panel. `from` acota el periodo (Metricas ofrece 30, 14 y 7
 * dias) y `to` lo cierra por arriba, que es lo que necesita la revision semanal:
 * una semana es un rango con dos extremos, no una cola que llega hasta hoy.
 * Sin ninguno de los dos, es todo el historial, y por eso existe `enabled`:
 * mientras no se sepa el rango, pedirlo sin el devolveria el historial entero
 * bajo el titulo de una semana.
 *
 * La clave lleva el rango dentro para que cada periodo tenga su cache, y empieza
 * por "summary" para que la invalidacion por prefijo siga alcanzandolos a todos.
 */
export function useSummary(from?: string, to?: string, enabled = true) {
  const owner = useOwnerId();

  return useQuery({
    queryKey: [...keys.summary, ownerKey(owner), from ?? "all", to ?? "all"] as const,
    queryFn: () => api.get<Summary>(`/api/v1/dashboard/summary${query({ from, to, owner })}`),
    enabled,
  });
}

/**
 * El desglose por una de las 14 dimensiones de SPEC 11.
 *
 * Una peticion por dimension: el servidor devuelve una sola y la pantalla pinta
 * la que el usuario esta mirando. Pedirlas las catorce para ensenar una seria
 * trece peticiones tiradas en una conexion de telefono.
 */
export function useBreakdown(dimension: BreakdownDimension, from?: string) {
  const owner = useOwnerId();

  return useQuery({
    queryKey: [...keys.breakdown, ownerKey(owner), dimension, from ?? "all"] as const,
    queryFn: () =>
      api.get<Breakdown>(`/api/v1/dashboard/breakdown${query({ dimension, from, owner })}`),
  });
}

/**
 * Los totales de pips y de R por semana ISO o por mes (ADR 0007). `from` acota
 * el periodo igual que en el resumen; sin el, es todo el historial.
 *
 * Cuando alguna fila suma pips de varios instrumentos, la respuesta lo dice en
 * `note`, y la pantalla lo pinta: un pip de GAIN 400 no es un pip de V75, y
 * esconderlo seria mentir con un numero.
 */
export function usePeriods(granularity: PeriodGranularity, from?: string, enabled = true) {
  const owner = useOwnerId();

  return useQuery({
    queryKey: [...keys.periods, ownerKey(owner), granularity, from ?? "all"] as const,
    queryFn: () =>
      api.get<PeriodTotals>(
        `/api/v1/dashboard/periods${query({ granularity, from, owner })}`,
      ),
    enabled,
  });
}

/**
 * Recalcular los pips derivados con el pip actual del instrumento (SPEC 19.6).
 * Con pips ESCRITOS el servidor no toca nada y lo dice en `note` (ADR 0007):
 * la respuesta se devuelve para que el detalle pueda ensenarlo.
 */
export function useRecalculateTrade(id: string) {
  const invalidate = useInvalidateTrades();

  return useMutation({
    mutationFn: (version: number) =>
      api.post<RecalculateResult>(`/api/v1/trades/${id}/recalculate`, { version }),
    onSuccess: () => invalidate(id),
  });
}

/**
 * Tras cualquier mutacion se invalidan el detalle, el listado y el panel. El
 * `version` del detalle cambia en cada escritura, y una pantalla con una version
 * vieja provocaria un 409 en el siguiente guardado.
 */
export function useInvalidateTrades() {
  const client = useQueryClient();

  return (id?: string) => {
    if (id !== undefined) {
      /* Sin dueno en el prefijo: el detalle que se escribio es el propio, pero
         un Administrador que lo miro antes con `?owner=` suyo cacheado seria la
         misma fila con otra clave. Invalidar todas las copias es mas barato que
         adivinar cual esta a la vista. */
      void client.invalidateQueries({
        predicate: (item) => item.queryKey[0] === "trade" && item.queryKey[2] === id,
      });
    }

    void client.invalidateQueries({ queryKey: ["trades"] });
    void client.invalidateQueries({ queryKey: keys.summary });
    void client.invalidateQueries({ queryKey: keys.equity });
    void client.invalidateQueries({ queryKey: keys.breakdown });
    void client.invalidateQueries({ queryKey: keys.periods });
    /* Y la campana, que es la razon por la que no hace falta consultarla en
       bucle: los avisos se derivan de estas mismas operaciones, asi que cerrar
       una o revisarla es justo el momento en que la respuesta cambia. */
    void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };
}

/**
 * El inicio de sesion, con usuario y contrasena (ADR 0005, punto 1).
 *
 * Las credenciales pasan por aqui y no se guardan en ningun sitio: lo unico que
 * queda es la cookie firmada que pone el servidor (SPEC 19.18).
 */
export type Credentials = { username: string; password: string };

export function useLogin() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (credentials: Credentials) =>
      api.post<void>("/api/v1/auth/login", credentials),
    onSuccess: () => client.invalidateQueries(),
  });
}

export function useLogout() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<void>("/api/v1/auth/logout"),
    onSuccess: () => client.clear(),
  });
}

export function useCreateTrade() {
  const invalidate = useInvalidateTrades();

  return useMutation({
    mutationFn: (body: unknown) =>
      api.post<Schemas["CreatedResponse"]>("/api/v1/trades", body),
    onSuccess: () => invalidate(),
  });
}

export function useFillTrade(id: string) {
  const invalidate = useInvalidateTrades();

  return useMutation({
    mutationFn: (body: unknown) => api.post<void>(`/api/v1/trades/${id}/fill`, body),
    onSuccess: () => invalidate(id),
  });
}

export function useCloseTrade(id: string) {
  const invalidate = useInvalidateTrades();

  return useMutation({
    mutationFn: (body: unknown) => api.post<void>(`/api/v1/trades/${id}/close`, body),
    onSuccess: () => invalidate(id),
  });
}

export function useCancelTrade(id: string) {
  const invalidate = useInvalidateTrades();

  return useMutation({
    mutationFn: (version: number) =>
      api.post<void>(`/api/v1/trades/${id}/cancel`, { version }),
    onSuccess: () => invalidate(id),
  });
}

/**
 * CORREGIR LAS FECHAS, solo el administrador. Lo pidio el propietario: «a veces
 * la fecha se guarda mal cuando ya esta cerrado el trade y no se puede hacer
 * nada».
 *
 * Invalida como cualquier otra escritura de la operacion porque mueve la
 * publicacion de DIA: el listado, el calendario y el panel la contaban en la
 * fecha vieja.
 */
export function useCorrectTradeMoments(id: string) {
  const invalidate = useInvalidateTrades();

  return useMutation({
    mutationFn: (body: unknown) => api.put<void>(`/api/v1/trades/${id}/moments`, body),
    onSuccess: () => invalidate(id),
  });
}

export function useReviewTrade(id: string) {
  const invalidate = useInvalidateTrades();

  return useMutation({
    mutationFn: (body: unknown) => api.put<void>(`/api/v1/trades/${id}/review`, body),
    onSuccess: () => invalidate(id),
  });
}

/**
 * CREAR un simbolo. El servidor lo tenia desde la Fase 1 y la pantalla no lo
 * ofrecia: el propietario pidio "la posibilidad de editar, crear y eliminar los
 * indices y pares".
 *
 * Invalida `lookups` y no solo la lista: el simbolo nuevo tiene que aparecer en
 * el desplegable del alta sin recargar, que es para lo que se crea.
 */
export function useCreateInstrument() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: unknown) => api.post<unknown>("/api/v1/settings/instrumentos", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.lookups });
      void client.invalidateQueries({ queryKey: ["instruments"] });
    },
  });
}

export function useUpdateInstrument(id: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: unknown) => api.put<void>(`/api/v1/settings/instrumentos/${id}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.lookups });
      // Tambien la lista de la pantalla: archivar un simbolo tiene que quitarlo
      // de la tabla sin recargar.
      void client.invalidateQueries({ queryKey: ["instruments"] });
    },
  });
}

/**
 * Los instrumentos. Con `includeArchived`, tambien los RETIRADOS.
 *
 * El parametro existe desde la Fase 1 en el servidor y la pantalla no lo pedia
 * nunca, asi que desmarcar "Activo" hacia desaparecer el simbolo sin dejar
 * ningun sitio donde encontrarlo. El propietario se encontro con eso de frente:
 * «acabo de desactivar volatility 10 y 25 y no los encuentro ya». La fila seguia
 * intacta en la base; lo que faltaba era pedirla (BUGLOG, 2026-10-09).
 *
 * El `includeArchived` va EN LA CLAVE. Sin el, las dos listas -- la de activos y
 * la de todos -- compartirian cache y encender el interruptor no traeria nada
 * nuevo hasta que la cache caducara.
 */
export function useInstruments(includeArchived = false) {
  return useQuery({
    queryKey: ["instruments", includeArchived],
    queryFn: () =>
      api.get<Instrument[]>(
        `/api/v1/settings/instrumentos${query({ includeArchived: includeArchived ? "true" : undefined })}`,
      ),
  });
}

/**
 * La curva de equity acumulada en R (SPEC 19.1). Es la lectura que mas dice del
 * panel: un "+4R acumulado" no distingue una subida limpia de una montana rusa
 * que acabo en el mismo sitio, y la forma si.
 */
export function useEquityCurve() {
  const owner = useOwnerId();

  return useQuery({
    queryKey: [...keys.equity, ownerKey(owner)] as const,
    queryFn: () =>
      api.get<EquityPoint[]>(`/api/v1/dashboard/equity-curve${query({ owner })}`),
  });
}

/* ========================================================================== */
/* Operaciones de sistema (ADR 0005, punto 3)                                 */
/* ========================================================================== */

/**
 * Salud de la base: tamanos por tabla, filas de prueba y almacen de imagenes.
 *
 * No se cachea: es la unica pantalla que existe para mirar cuanto ocupa la base
 * AHORA, y un numero de hace cinco minutos justo despues de generar cinco mil
 * operaciones seria una mentira con formato de dato.
 */
export function useDatabaseHealth() {
  return useQuery({
    queryKey: keys.databaseHealth,
    queryFn: () => api.get<DatabaseHealth>("/api/v1/system/database"),
    staleTime: 0,
  });
}

/**
 * Las cuatro operaciones que cambian la base entera invalidan TODA la cache, no
 * solo su propia clave: despues de un reinicio de fabrica o de una restauracion
 * no queda ni una lectura que siga siendo cierta, y una pantalla que siguiera
 * ensenando la operacion de ayer estaria ensenando una fila que ya no existe.
 */
function useWholeDatabaseMutation<TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: run,
    onSuccess: () => client.invalidateQueries(),
  });
}

export function useFactoryReset() {
  return useWholeDatabaseMutation((confirmationPhrase: string) =>
    api.post<FactoryResetResult>("/api/v1/system/reset", { confirmationPhrase }),
  );
}

export function useGenerateDemoData() {
  return useWholeDatabaseMutation((tradeCount: number) =>
    api.post<DemoDataResult>("/api/v1/system/demo-data", { tradeCount }),
  );
}

export function useDeleteDemoData() {
  /* `void` explicito y no inferido: sin el, el tipo del argumento sale `unknown`
     y `mutate()` no compila sin pasarle un valor que no significa nada. */
  return useWholeDatabaseMutation<void, DemoDataRemoved>(() =>
    api.del<DemoDataRemoved>("/api/v1/system/demo-data"),
  );
}

/**
 * La restauracion es el unico sitio de la aplicacion que sube un archivo, y va
 * en `multipart/form-data` porque asi lo declara el contrato: los dos campos se
 * llaman `file` y `confirmationPhrase`, y de esos nombres depende que el 400
 * caiga junto al campo que lo provoco.
 */
export function useRestoreDatabase() {
  return useWholeDatabaseMutation((input: { file: File; confirmationPhrase: string }) => {
    const form = new FormData();

    form.append("file", input.file);
    form.append("confirmationPhrase", input.confirmationPhrase);

    return api.postForm<DatabaseRestore>("/api/v1/system/restore", form);
  });
}

/**
 * El volcado no invalida nada: leer no cambia la base. Es una mutacion de
 * TanStack Query solo por lo que da a cambio, que es `isPending` y `error`: el
 * respaldo tarda y puede fallar porque falte `pg_dump`, y las dos cosas tienen
 * que verse en la pantalla.
 */
export function useDownloadBackup() {
  return useMutation({
    mutationFn: () => api.download("/api/v1/system/backup"),
  });
}

/* ========================================================================== */
/* Usuarios y sesion (ADR 0005, puntos 1 y 2)                                 */
/* ========================================================================== */

/**
 * El listado de usuarios. No trae ni hash ni sal: solo `hasPassword`, que es lo
 * unico que la pantalla necesita saber de la contrasena de otro.
 *
 * Solo responde a un Administrador; para los demas es un 403, y la pantalla ni
 * se ofrece en el menu.
 */
export function useUsers(enabled = true) {
  return useQuery({
    queryKey: keys.users,
    queryFn: () => api.get<User[]>("/api/v1/users"),
    enabled,
  });
}

/**
 * Cualquier cambio en un usuario invalida TAMBIEN la sesion: un administrador
 * que se cambia el nombre o se quita el rol a si mismo tiene que verlo en el
 * sidebar sin recargar, y el servidor relee la fila en cada peticion, asi que la
 * sesion que tenemos en cache ya no es la que manda (ADR 0005, punto 3).
 */
function useUserMutation<TInput, TResult>(run: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users });
      void client.invalidateQueries({ queryKey: keys.session });
    },
  });
}

export function useCreateUser() {
  return useUserMutation((body: unknown) =>
    api.post<Schemas["CreatedResponse"]>("/api/v1/users", body),
  );
}

export function useUpdateUser(id: string) {
  return useUserMutation((body: unknown) => api.put<void>(`/api/v1/users/${id}`, body));
}

/**
 * La contrasena de otro se pone, no se lee. Va en su propia peticion y no en la
 * edicion: asi cambiar un rol no obliga a teclear una contrasena, y teclear una
 * contrasena no puede cambiar un rol sin querer.
 */
export function useChangeUserPassword(id: string) {
  return useUserMutation((body: { password: string; version: number }) =>
    api.put<void>(`/api/v1/users/${id}/password`, body),
  );
}

export function useSetUserActive(id: string) {
  return useUserMutation((active: boolean) =>
    api.post<void>(`/api/v1/users/${id}/${active ? "activate" : "deactivate"}`),
  );
}

/** Borrado LOGICO: la fila se queda con su historial y el acceso desaparece. */
export function useDeleteUser(id: string) {
  return useUserMutation(() => api.del<void>(`/api/v1/users/${id}`));
}

/**
 * Los minutos de inactividad tras los que el servidor cierra la sesion. El
 * valor vive en la base y no en configuracion, para poder cambiarlo sin
 * reiniciar el servicio (ADR 0005, punto 2).
 */
export function useSessionPolicy(enabled = true) {
  return useQuery({
    queryKey: keys.sessionPolicy,
    queryFn: () => api.get<SessionPolicy>("/api/v1/system/session-policy"),
    enabled,
  });
}

export function useUpdateSessionPolicy() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: { inactivityMinutes: number; version: number }) =>
      api.put<void>("/api/v1/system/session-policy", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.sessionPolicy });
      /* La sesion lleva dentro los minutos de inactividad, y el aviso los usa
         para saber cuando avisar. */
      void client.invalidateQueries({ queryKey: keys.session });
    },
  });
}

/**
 * "Seguir dentro": una peticion, pedida a mano, que reinicia el reloj de
 * inactividad del servidor.
 *
 * Es una mutacion aunque sea un GET, porque lo que se quiere de ella es
 * `isPending` y `error`. Y es la UNICA forma de reiniciar ese reloj desde el
 * aviso: si la aplicacion lo hiciera sola cada pocos minutos, una pestana
 * olvidada mantendria la sesion viva para siempre y el cierre por inactividad
 * no cerraria nada.
 */
export function useKeepSessionAlive() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => api.get<Schemas["SessionResponse"]>("/api/v1/auth/session"),
    onSuccess: (data) => client.setQueryData(keys.session, data),
  });
}

/**
 * LOS NUMEROS QUE EL ALTA OFRECE A UN TOQUE: pips de riesgo, pips de objetivo y
 * lotaje (ADR 0011).
 *
 * Los tres campos en UNA peticion, no tres. El alta necesita los tres a la vez y
 * son una docena de filas en total: tres peticiones serian tres latencias para
 * ahorrar unos bytes.
 *
 * SIN DUENO en la clave, al contrario que casi todo lo demas: la tabla no lo
 * tiene -- es la lista del sistema, como las sugerencias de texto -- y meterlo en
 * la clave crearia una cache por usuario para una respuesta que es la misma.
 *
 * `staleTime` largo porque esto cambia cuando el propietario entra a Ajustes a
 * cambiarlo, y entonces la propia pantalla invalida la clave.
 */
export function useQuickValues() {
  return useQuery({
    queryKey: ["quick-values"],
    queryFn: () => api.get<QuickValue[]>("/api/v1/settings/valores-rapidos"),
    staleTime: 10 * 60_000,
  });
}

export type QuickValue = Schemas["QuickValueDto"];
export type QuickValueField = Schemas["QuickValueField"];
